/* What /checks decides, kept apart from what it draws.
 *
 * Two decisions live here: how the fifteen checks are grouped, and what a muted
 * check says about itself. Both are about data, so both are asserted by
 * tools/pages-check.tsx without a browser.
 */

import { PLAN_AREA_IDS, PLAN_AREA_LABELS } from '../data/planAreas';
import { dismissReasonBecause } from '../data/review';
import type { FailureMode } from '../data/taxonomy';
import type { MutedCheck, PlanAreaId } from '../types';

export interface CheckGroup {
  area: PlanAreaId;
  label: string;
  /** How many checks the area holds in total, before any filter. */
  total: number;
  /** Those of them the current filters leave visible. */
  modes: FailureMode[];
}

/**
 * The checks, grouped by the same plan areas the workspace groups findings by,
 * so a reviewer moving between the two pages meets one taxonomy rather than
 * two.
 *
 * Ordered canonically rather than by size. The workspace sorts its groups by
 * how many findings each holds, because there the count is news; here it is
 * fixed for the life of the catalogue, and sorting by it would only bury the
 * arithmetic checks in the middle of the page.
 */
export function groupChecksByPlanArea(
  modes: FailureMode[],
  visible: FailureMode[] = modes,
): CheckGroup[] {
  const visibleIds = new Set(visible.map((fm) => fm.id));

  return PLAN_AREA_IDS.map((area) => {
    const all = modes.filter((fm) => fm.planArea === area);
    return {
      area,
      label: PLAN_AREA_LABELS[area],
      total: all.length,
      modes: all.filter((fm) => visibleIds.has(fm.id)),
    };
  }).filter((group) => group.modes.length > 0);
}

/**
 * What a muted check says about itself, on the page that turns it back on.
 *
 * A sentence rather than a badge: the reviewer muted it weeks ago in another
 * review and will not remember why unless they are told. A check switched off
 * here rather than offered after three dismissals has no recurring reason
 * behind it, and says that instead of inventing one.
 */
export function mutedSentence(entry: MutedCheck): string {
  if (entry.source === 'reference' || entry.count === 0) {
    return 'Muted in this browser. You turned it off here rather than after setting its findings aside.';
  }
  return `Muted in this browser. You had marked it not a concern ${entry.count} times, each time because ${dismissReasonBecause(entry.reason)}.`;
}
